"use client";

import React, { useEffect, useState } from "react";
import { 
  Calendar, 
  User, 
  Clock, 
  ArrowRight, 
  Share2, 
  Tag, 
  ChevronLeft 
} from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";



function formatDate(dateString?: string) {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}



function RelatedPostsConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div data-section-id="20873" className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-80 animate-pulse rounded-none bg-muted/20" />
        ))}
      </div>
    );
  }

  if (error || posts.length === 0) {
    return null; 
  }

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
      {posts.map((post, i) => (
        <Card key={post.id} data-index={i} className="flex h-full flex-col overflow-hidden border-none bg-card/40 shadow-sm transition-all hover:shadow-lg hover:bg-card/60">
          {post.featuredImage && (
            <div className="aspect-video w-full overflow-hidden">
              <img
                src={post.featuredImage}
                alt={post.title}
                className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
              />
            </div>
          )}
          <CardHeader className="p-5 pb-2">
            <div className="mb-2 flex items-center gap-2 text-xs tracking-widest uppercase text-muted-foreground">
              <Calendar className="h-3 w-3" />
              <span>{formatDate(post.date)}</span>
            </div>
            <CardTitle className="font-serif text-xl leading-tight text-foreground">
              <Link to={post.link || "#"} className="hover:text-primary transition-colors duration-300">
                {post.title}
              </Link>
            </CardTitle>
          </CardHeader>
          <CardContent className="grow p-5 pt-2">
            <CardDescription className="line-clamp-3 text-sm text-muted-foreground/80 italic">
              {post.excerpt ? (
                <span dangerouslySetInnerHTML={{ __html: post.excerpt }} />
              ) : (
                "Explore the soulful rhythms and behind-the-scenes stories of Synergy Sound..."
              )}
            </CardDescription>
          </CardContent>
          <CardFooter className="p-5 pt-0">
            <Link to={post.link || "#"} className="group flex items-center text-xs font-bold tracking-widest uppercase text-primary">
              Read More 
              <ArrowRight className="ml-2 h-3 w-3 transition-transform group-hover:translate-x-1" />
            </Link>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}

function RelatedPosts({ currentPostId, categoryIds }: { currentPostId: string; categoryIds: number[] }) {
  
  const relatedQuery = new WP_Query({
    post_type: "post",
    posts_per_page: 3,
    post__not_in: [currentPostId],
    category__in: categoryIds,
    orderby: "date",
    order: "DESC"
  });

  if (categoryIds.length === 0) return null;

  return (
    <div className="mt-16">
      <div className="mb-8 flex items-center justify-between border-b border-border/50 pb-4">
        <h3 className="font-serif text-2xl font-bold text-foreground md:text-4xl tracking-tight">
          Musical Insights
        </h3>
        <Link to="#">
          <Button variant="ghost" className="hidden sm:flex text-primary hover:text-primary hover:bg-primary/5 tracking-widest uppercase text-xs">
            The Full Journal <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      </div>
      <PostsProvider wp_query={relatedQuery}>
        <RelatedPostsConsumer />
      </PostsProvider>
    </div>
  );
}



function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-20">
        <div className="mb-8 h-12 w-3/4 animate-pulse rounded bg-muted/20" />
        <div className="mb-12 h-96 w-full animate-pulse rounded bg-muted/20" />
        <div className="space-y-4">
          <div className="h-4 w-full animate-pulse rounded bg-muted/20" />
          <div className="h-4 w-full animate-pulse rounded bg-muted/20" />
          <div className="h-4 w-2/3 animate-pulse rounded bg-muted/20" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto flex min-h-[50vh] flex-col items-center justify-center px-4 text-center">
        <h2 className="mb-4 font-serif text-3xl font-bold text-destructive">Encore Delayed</h2>
        <p className="text-muted-foreground">{error}</p>
        <Link to="/HomePage" className="mt-6">
          <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none px-8">Return Home</Button>
        </Link>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="container mx-auto flex min-h-[50vh] flex-col items-center justify-center px-4 text-center">
        <h2 className="mb-4 font-serif text-3xl font-bold">Performance Not Found</h2>
        <p className="text-muted-foreground">The article you are looking for has left the stage.</p>
        <Link to="/HomePage" className="mt-6">
          <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none px-8">Return Home</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];
  const categoryIds = post.categories?.map((c) => c.id) || [];

  return (
    <article className="container mx-auto max-w-4xl px-4 py-12 md:py-20">
      {}
      <div className="mb-8">
        <Link to="#" className="inline-flex items-center text-xs font-bold tracking-widest uppercase text-muted-foreground hover:text-primary transition-colors">
          <ChevronLeft className="mr-1 h-4 w-4" />
          Back to Journal
        </Link>
      </div>

      {}
      <header className="mb-10 text-center md:mb-14">
        <div className="mb-6 flex flex-wrap items-center justify-center gap-3 text-xs font-bold tracking-widest uppercase text-primary">
          {post.categories?.map((cat, i) => (
            <span key={cat.id} data-index={i} className="rounded-none border border-primary/30 bg-primary/5 px-4 py-1 text-primary">
              {cat.name}
            </span>
          ))}
        </div>
        
        <h1 className="font-serif text-4xl font-bold leading-tight text-foreground md:text-6xl lg:text-7xl tracking-tighter">
          {post.title}
        </h1>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-xs tracking-widest uppercase text-muted-foreground">
          {post.author && (
            <div className="flex items-center gap-2">
              <Avatar className="h-10 w-10 border border-primary/20">
                <AvatarImage src={post.author.avatar?.["96"]} alt={post.author.name} />
                <AvatarFallback className="bg-secondary text-primary"><User className="h-4 w-4" /></AvatarFallback>
              </Avatar>
              <span className="font-bold text-foreground">{post.author.name}</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-primary" />
            <span>{formatDate(post.date)}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-primary" />
            <span>4 min read</span>
          </div>
        </div>
      </header>

      {}
      {post.featuredImage && (
        <div className="mb-12 overflow-hidden rounded-none shadow-2xl md:mb-16 border border-border/20">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="h-auto w-full object-cover"
          />
        </div>
      )}

      {}
      <div className="prose prose-lg prose-stone dark:prose-invert mx-auto max-w-none font-sans leading-relaxed selection:bg-primary/30">
        {}
        <div dangerouslySetInnerHTML={{ __html: post.content || "" }} />
      </div>

      {}
      <div className="mt-12 border-t border-border/50 pt-8">
        <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-center">
          <div className="flex flex-wrap items-center gap-2">
            <Tag className="h-4 w-4 text-primary" />
            <span className="mr-2 text-xs font-bold tracking-widest uppercase text-muted-foreground">Notes:</span>
            {post.tags && post.tags.length > 0 ? (
              post.tags.map((tag, i) => (
                <Link 
                  key={tag.id} 
                  data-index={i}
                  to={tag.link || "#"} 
                  className="text-xs font-medium text-muted-foreground hover:text-primary transition-colors underline underline-offset-4 decoration-primary/30"
                >
                  #{tag.name}
                </Link>
              ))
            ) : (
              <span className="text-xs text-muted-foreground italic">Uncategorized melody</span>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            <span className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Share:</span>
            <Button variant="outline" size="icon" className="h-9 w-9 rounded-full border-primary/20 hover:bg-primary/10 hover:text-primary transition-all">
              <Share2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {}
      {post.author && (
        <div className="mt-12 rounded-none bg-secondary/20 p-8 border-l-4 border-primary">
          <div className="flex flex-col items-center gap-6 text-center sm:flex-row sm:text-left">
            <Avatar className="h-24 w-24 border-2 border-primary/20 shadow-lg">
              <AvatarImage src={post.author.avatar?.["96"]} alt={post.author.name} />
              <AvatarFallback className="text-2xl bg-card text-primary"><User className="h-10 w-10" /></AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <h3 className="font-serif text-2xl font-bold text-foreground">Meet the Artist</h3>
              <p className="mt-2 text-muted-foreground leading-relaxed italic">
                A seasoned musician and bandleader dedicated to bringing the soulful rhythms of Los Angeles to the world's most elegant and high-end stages.
              </p>
              {post.author.link && (
                <Link to={post.author.link} className="mt-4 inline-block text-xs font-bold tracking-widest uppercase text-primary hover:text-primary/80 transition-colors">
                  More From {post.author.name}
                </Link>
              )}
            </div>
          </div>
        </div>
      )}

      {}
      <RelatedPosts currentPostId={post.id} categoryIds={categoryIds} />
    </article>
  );
}



export default function SinglePostTemplateSection() {
  
  
  const currentParams = typeof window !== 'undefined' && window.wvcClient 
    ? window.wvcClient.getCurrentWPQueryParams() 
    : { post_type: 'post', p: 1 }; 

  
  const wp_query = new WP_Query(currentParams);

  return (
    <section id="single-post-template-section" className="dark bg-background text-foreground selection:bg-primary/30">
      <PostsProvider wp_query={wp_query}>
        <SinglePostContent />
      </PostsProvider>
    </section>
  );
}